import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { getTheme, ThemeVariant } from '../theme';

interface ThemeContextProps {
  mode: PaletteMode;
  variant: ThemeVariant;
  toggleMode: () => void;
  setMode: (mode: PaletteMode) => void;
  setVariant: (variant: ThemeVariant) => void;
}

const ThemeContext = createContext<ThemeContextProps>({
  mode: 'dark',
  variant: 'company',
  toggleMode: () => {},
  setMode: () => {},
  setVariant: () => {},
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Try to get mode from localStorage, default to 'dark'
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('theme-mode');
    return (savedMode === 'dark' || savedMode === 'light') ? savedMode : 'dark';
  });

  // Per-tier variant (set from the logged-in user's tier by ThemeTierSync).
  const [variant, setVariantState] = useState<ThemeVariant>(() => {
    const saved = localStorage.getItem('theme-variant');
    return (saved === 'employee' || saved === 'company' || saved === 'owner') ? saved : 'company';
  });

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Switching tier also sets the base mode for that tier (employees get the
  // light scheme; company/owner default to dark). The manual mode toggle in
  // Settings/Profile can still override afterwards.
  const setVariant = useCallback((next: ThemeVariant) => {
    setVariantState((prev) => {
      if (prev === next) return prev;
      localStorage.setItem('theme-variant', next);
      setMode(next === 'employee' ? 'light' : 'dark');
      return next;
    });
  }, []);

  // Get the theme object based on current mode + tier variant
  const theme = getTheme(mode, variant);

  return (
    <ThemeContext.Provider value={{ mode, variant, toggleMode, setMode, setVariant }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
